import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { colors } from "../../theme/colors";
import {
    ACCOUNT_PAGE_FIELD_LABEL_SX,
    ACCOUNT_PAGE_SUMMARY_COLUMN_SX,
    ACCOUNT_PAGE_SUMMARY_DIVIDER_SX,
    ACCOUNT_PAGE_SUMMARY_PROGRESS_SEGMENT_SX,
    ACCOUNT_PAGE_SUMMARY_PROGRESS_TRACK_SX,
    ACCOUNT_PAGE_SUMMARY_SCORE_SX,
    ACCOUNT_PAGE_SUMMARY_STATUS_CHIP_SX,
    ACCOUNT_PAGE_SUMMARY_SX,
} from "./accountPageStyles";
import {
    ACCOUNT_STATUS_OPTIONS,
    formatProfileTrustTier,
    getAccountStatusChipStyle,
    getAccountStatusLabel,
    getProfileTrustFactor,
} from "./accountProfileSummaryUtils";

const STRENGTH_SEGMENTS = 10;

function AccountProfileSummary({ user }) {
    const accountStatus = user?.accountStatus;
    const statusStyle = getAccountStatusChipStyle(accountStatus);
    const statusOption = ACCOUNT_STATUS_OPTIONS.find(
        (option) => option.id === accountStatus,
    );
    const statusLabel = statusOption?.label || getAccountStatusLabel(user);
    const score = getProfileTrustFactor(user);
    const filledSegs = Math.round((score / 10) * STRENGTH_SEGMENTS);
    const trustTier = formatProfileTrustTier(user?.profileTrustTier);
    const onboardingTier = Number(user?.completedOnboardingTier) || 0;

    const strengthHint = useMemo(() => {
        const parts = [];
        if (onboardingTier > 0) {
            parts.push(`Tier ${onboardingTier}`);
        }
        if (trustTier) {
            parts.push(trustTier);
        }
        return parts.join(" · ");
    }, [onboardingTier, trustTier]);

    const fillColor =
        score >= 7 ? colors.state.success.main : colors.text.muted;

    return (
        <Box sx={ACCOUNT_PAGE_SUMMARY_SX} aria-label="Account overview">
            <Box sx={ACCOUNT_PAGE_SUMMARY_COLUMN_SX}>
                <Typography sx={ACCOUNT_PAGE_FIELD_LABEL_SX}>Account status</Typography>
                <Box
                    id="account-status"
                    aria-label={`Account status: ${statusLabel}`}
                    sx={{
                        ...ACCOUNT_PAGE_SUMMARY_STATUS_CHIP_SX,
                        borderColor: statusStyle.borderColor,
                        backgroundColor: statusStyle.backgroundColor,
                        color: statusStyle.color,
                    }}
                >
                    {statusLabel}
                </Box>
            </Box>

            <Box sx={ACCOUNT_PAGE_SUMMARY_DIVIDER_SX} aria-hidden />

            <Box sx={{ ...ACCOUNT_PAGE_SUMMARY_COLUMN_SX, flex: 1, minWidth: 0 }}>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "0.75rem",
                        width: "100%",
                    }}
                >
                    <Typography sx={ACCOUNT_PAGE_FIELD_LABEL_SX}>Profile strength</Typography>
                    <Typography sx={ACCOUNT_PAGE_SUMMARY_SCORE_SX}>
                        {score.toFixed(2)}
                        <Box
                            component="span"
                            sx={{
                                color: colors.text.muted,
                                fontWeight: 500,
                                fontSize: "0.78rem",
                            }}
                        >
                            {" "}
                            / 10
                        </Box>
                    </Typography>
                </Box>

                <Box sx={ACCOUNT_PAGE_SUMMARY_PROGRESS_TRACK_SX}>
                    {Array.from({ length: STRENGTH_SEGMENTS }).map((_, index) => (
                        <Box
                            key={`strength-seg-${index}`}
                            sx={{
                                ...ACCOUNT_PAGE_SUMMARY_PROGRESS_SEGMENT_SX,
                                background:
                                    index < filledSegs
                                        ? `linear-gradient(90deg, ${alpha(fillColor, 0.55)}, ${fillColor})`
                                        : "rgba(255,255,255,0.08)",
                            }}
                        />
                    ))}
                </Box>

                {strengthHint ? (
                    <Typography
                        sx={{
                            color: colors.text.muted,
                            fontSize: "0.75rem",
                            fontWeight: 500,
                            lineHeight: 1.4,
                            letterSpacing: "0.02em",
                        }}
                    >
                        {strengthHint}
                    </Typography>
                ) : null}
            </Box>
        </Box>
    );
}

AccountProfileSummary.propTypes = {
    user: PropTypes.object,
};

export default React.memo(AccountProfileSummary);
